/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { Observable, from } from "rxjs"
import axios, {
  CancelToken,
  AxiosError,
} from "axios"
import forEachObjIndexed from "ramda/src/forEachObjIndexed"
import { CommonAPIError } from "../constants/network_errors"
import { storageHelper } from '../../utils/storage'
import { getCsrfHeader } from "../../utils/csrf"

type PathRewriter = (path: string) => string
type APIHostGetter = () => string
type HTTP401UnauthorizedHandler = () => void

export interface JWTAccessible {
  readonly jwt: string
}

interface HTTPPayload  {
  [index: string]:
    | string
    | number
    | undefined
    | null
    | number[]
    | string[]
    | boolean
}

interface GETParams {
  endpoint: string
  headers?: { [index: string]: string }
  params?: HTTPPayload
  cancelToken?: CancelToken
  timeout?: number
  withAuthenticationHeader?: boolean
}

interface POSTParams {
  endpoint: string
  data?: HTTPPayload
  headers?: { [index: string]: string }
  withAuthenticationHeader?: boolean
  cancelToken?: CancelToken
  timeout?: number
}

interface PUTParams {
  endpoint: string
  data?: HTTPPayload
  headers?: { [index: string]: string }
  withAuthenticationHeader?: boolean
  cancelToken?: CancelToken
  timeout?: number
}

type APIResponse<T> = {
  data: T | null
  errorMsg: string | null
  httpCode: number
  cancelled: boolean
}

export interface HTTPRequestFactoryConfig {
  jwtAccessible: JWTAccessible
  apiHostGetter?: APIHostGetter
  prefix?: string
  baseTimeout?: number
  pathRewriter?: PathRewriter
  http401UnauthorizedHandler?: HTTP401UnauthorizedHandler
}

const defaultPathRewriter = (path: string) => path
const defaultAPIHostGetter = () =>
  window.config.api.host

export class HTTPRequestFactory {
  private jwtAccessible: JWTAccessible

  private apiHostGetter: APIHostGetter

  private prefix: string

  private baseTimeout: number

  private pathRewriter: PathRewriter

  private http401UnauthorizedHandler?: HTTP401UnauthorizedHandler

  private isAuthWithCookie: boolean

  constructor({
    jwtAccessible,
    apiHostGetter,
    prefix,
    baseTimeout,
    pathRewriter,
    http401UnauthorizedHandler,
  }: HTTPRequestFactoryConfig) {
    this.jwtAccessible = jwtAccessible
    this.apiHostGetter =
      apiHostGetter ?? defaultAPIHostGetter
    this.prefix = prefix ?? ""
    this.baseTimeout = baseTimeout ?? 5 * 1000
    this.pathRewriter =
      pathRewriter ?? defaultPathRewriter
    this.http401UnauthorizedHandler = http401UnauthorizedHandler
    this.GET = this.GET.bind(this)
    this.POST = this.POST.bind(this)
    this.PUT = this.PUT.bind(this)
    this.isAuthWithCookie = storageHelper.isCookieAuthEnabled();
  }

  GET<T>({
    endpoint,
    cancelToken,
    timeout,
    headers = {},
    params = {},
    withAuthenticationHeader = true,
  }: GETParams): Observable<APIResponse<T>> {
    const uri = `${this.apiHostGetter()}/${
      this.prefix
    }${this.pathRewriter(endpoint)}`
    const requestHeaders = {
      ...headers,
    }
    if (withAuthenticationHeader && !this.isAuthWithCookie) {
      const authenticateToken = this.jwtAccessible
        .jwt
      requestHeaders.Authorization = authenticateToken
    }
    const finalParams: {
      [index: string]: any
    } = {}
    forEachObjIndexed((val, key) => {
      if (val !== undefined && val !== null) {
        finalParams[key] = val
      }
    }, params)
    return from(
      axios
        .get<T>(uri, {
          cancelToken,
          headers: requestHeaders,
          params: finalParams,
          timeout: timeout ?? this.baseTimeout,
          withCredentials: this.isAuthWithCookie,
        })
        .then(
          res =>
            ({
              data: res.data,
              errorMsg: null,
              httpCode: res.status,
            } as APIResponse<T>),
        )
        .catch((err: AxiosError) => {
          const cancelled = axios.isCancel(err)
          const httpCode = err.response
            ? err.response.status
            : 0
          const errMsg = err.message
          const errorMsg = cancelled
            ? CommonAPIError.cancelled
            : errMsg ||
              CommonAPIError.unknown
          if (
            httpCode === 401 &&
            this.http401UnauthorizedHandler
          ) {
            this.http401UnauthorizedHandler()
          }
          return {
            cancelled,
            data: null,
            errorMsg,
            httpCode,
          } as APIResponse<T>
        }),
    )
  }

  POST<T>({
    endpoint,
    data = {},
    headers = {},
    withAuthenticationHeader = true,
    timeout,
    cancelToken,
  }: POSTParams): Observable<APIResponse<T>> {
    const uri = `${this.apiHostGetter()}/${
      this.prefix
    }${this.pathRewriter(endpoint)}`
    const requestHeaders = {
      ...headers,
    }
    if (withAuthenticationHeader && !this.isAuthWithCookie) {
      const authenticateToken = this.jwtAccessible
        .jwt
      requestHeaders.Authorization = authenticateToken
    }
    if (this.isAuthWithCookie) {
      Object.assign(requestHeaders, getCsrfHeader("post"))
    }
    return from(
      axios
        .post<T>(uri, data, {
          cancelToken,
          headers: requestHeaders,
          timeout: timeout ?? this.baseTimeout,
          withCredentials: this.isAuthWithCookie,
        })
        .then(
          res =>
            ({
              data: res.data,
              errorMsg: null,
              httpCode: res.status,
            } as APIResponse<T>),
        )
        .catch((err: AxiosError) => {
          const cancelled = axios.isCancel(err)
          const httpCode = err.response
            ? err.response.status
            : 0
          const errMsg = err.message
          const errorMsg = cancelled
            ? CommonAPIError.cancelled
            : errMsg ||
              CommonAPIError.unknown
          if (
            httpCode === 401 &&
            this.http401UnauthorizedHandler
          ) {
            this.http401UnauthorizedHandler()
          }
          return {
            cancelled,
            data: err.response?.data || null,
            errorMsg,
            httpCode,
          } as APIResponse<T>
        }),
    )
  }

  PUT<T>({
    endpoint,
    data = {},
    headers = {},
    withAuthenticationHeader = true,
    timeout,
    cancelToken,
  }: PUTParams): Observable<APIResponse<T>> {
    const uri = `${this.apiHostGetter()}/${
      this.prefix
    }${this.pathRewriter(endpoint)}`
    const requestHeaders = {
      ...headers,
    }
    if (withAuthenticationHeader && !this.isAuthWithCookie) {
      const authenticateToken = this.jwtAccessible
        .jwt
      requestHeaders.Authorization = authenticateToken
    }
    if (this.isAuthWithCookie) {
      Object.assign(requestHeaders, getCsrfHeader("put"))
    }
    return from(
      axios
        .put<T>(uri, data, {
          cancelToken,
          headers: requestHeaders,
          timeout: timeout ?? this.baseTimeout,
          withCredentials: this.isAuthWithCookie,
        })
        .then(
          res =>
            ({
              data: res.data,
              errorMsg: null,
              httpCode: res.status,
            } as APIResponse<T>),
        )
        .catch((err: AxiosError) => {
          const cancelled = axios.isCancel(err)
          const httpCode = err.response
            ? err.response.status
            : 0
          const errMsg = err.message
          const errorMsg = cancelled
            ? CommonAPIError.cancelled
            : errMsg ||
              CommonAPIError.unknown
          if (
            httpCode === 401 &&
            this.http401UnauthorizedHandler
          ) {
            this.http401UnauthorizedHandler()
          }
          return {
            cancelled,
            data: err.response?.data || null,
            errorMsg,
            httpCode,
          } as APIResponse<T>
        }),
    )
  }
}
